import styled from '@emotion/styled';
import { TitleLarge, SubtitleMedium } from '../components/Typography';
import { colors, typography } from '../theme/tokens';
import Button from '../components/Button';
import { Plus, Sparkle, MagnifyingGlass, ArrowSquareOut } from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { MealPlan } from '../types/mealPlan';
import noImage from '../assets/no-image.png';

const MealPlansContainer = styled.div`
  min-height: 100vh;
  padding: 1rem;
  overflow-y: auto;
`;

const MealPlansHeader = styled.div`
  background-color: ${colors.neutral.white};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const TitleSection = styled.div``;

const MealPlansTitle = styled(TitleLarge)`
  color: ${colors.neutral.black};
  margin-bottom: 1rem;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-top: 1rem;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;

  input {
    width: 100%;
    padding: 0.75rem 2.5rem 0.75rem 0.75rem;
    border: 1px solid ${colors.neutral.lightCharcoal};
    border-radius: 4px;
    font-size: 1rem;
    margin-bottom:0;
    background-color: ${colors.neutral.white};
    color: ${colors.neutral.black};
    
    &:focus {
      outline: none;
      border-color: ${colors.brand.laurel};
    }
  }

  svg {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: ${colors.neutral.lightCharcoal};
    width: 20px;
    height: 20px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
`;

const AIButton = styled(Button)`
  position: relative;
  background: ${colors.brand.darkLaurel} !important;
  color: ${colors.neutral.white} !important;
  
  &:hover {
    background: ${colors.brand.laurel} !important;
    color: ${colors.neutral.white} !important;
  }
`;

const TableContainer = styled.div`
  background-color: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem 2rem;
  border-bottom: 1px solid ${colors.neutral.darkCharcoal};
  color: ${colors.neutral.darkCharcoal};
  font-family: ${typography.label.large.fontFamily};
  font-size: ${typography.label.large.fontSize};
  line-height: ${typography.label.large.lineHeight};
  background-color: ${colors.neutral.lightCharcoal};
`;

const Td = styled.td`
  padding: 1rem 2rem;
  border-bottom: 1px solid ${colors.neutral.offWhite};
  color: ${colors.neutral.darkCharcoal};
  font-family: 'Gabarito', sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.neutral.offWhite};
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.neutral.offWhite};
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.brand.laurel};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  transition: all 0.2s;
  font-family: 'Gabarito', sans-serif;
  font-size: 14px;

  &:hover {
    background-color: ${colors.neutral.offWhite};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.neutral.darkCharcoal};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.reds.bright};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.neutral.darkCharcoal};
`;

const LoadMoreButton = styled(Button)`
  margin: 2rem auto;
  display: block;
`;

const MealsList = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const MealTag = styled.span`
  background-color: ${colors.brand.laurel}20;
  color: ${colors.brand.laurel};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const CoverImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 4px;
  object-fit: cover;
`;

interface MealPlanWithDetails extends MealPlan {
  duration: number;
  meals: string[];
}

export default function MealPlans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mealPlans, setMealPlans] = useState<MealPlanWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const pageSize = 10;

  const fetchMealPlans = async (pageNum: number = 1, append: boolean = false) => {
    if (!user) return;

    try {
      if (pageNum === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      // Get the creator's ID from CreatorDetails using the auth user's ID
      const { data: creatorDetails, error: creatorError } = await supabase
        .from('CreatorDetails')
        .select('id')
        .eq('AuthID', user.id)
        .single();

      if (creatorError) {
        throw new Error('Failed to verify creator account');
      }

      if (!creatorDetails) {
        throw new Error('No creator account found for the current user');
      }

      const creatorId = creatorDetails.id;

      // Fetch meal plans with pagination
      let query = supabase
        .from('meal_plans')
        .select('*')
        .eq('creator_id', creatorId)
        .order('created_at', { ascending: false });

      if (searchQuery) {
        query = query.ilike('title', `%${searchQuery}%`);
      }

      const { data, error } = await query
        .range((pageNum - 1) * pageSize, pageNum * pageSize - 1);

      if (error) {
        throw error;
      }

      if (data) {
        // For each meal plan, fetch the weeks and meals to calculate duration and meal types
        const mealPlansWithDetails = await Promise.all(
          data.map(async (mealPlan) => {
            // Get weeks count (duration)
            const { data: weeks } = await supabase
              .from('meal_plan_weeks')
              .select('id')
              .eq('meal_plan_id', mealPlan.id);

            // Get unique meal types for this plan
            const { data: meals } = await supabase
              .from('meal_plan_meals')
              .select('meal_type')
              .eq('day_id', (await supabase
                .from('meal_plan_days')
                .select('id')
                .eq('week_id', weeks?.[0]?.id || '')
                .limit(1)
                .single()).data?.id || '');

            const uniqueMeals = [...new Set(meals?.map(m => m.meal_type) || [])];

            return {
              ...mealPlan,
              duration: weeks?.length || 0,
              meals: uniqueMeals
            };
          })
        );

        if (append) {
          setMealPlans(prev => [...prev, ...mealPlansWithDetails]);
        } else {
          setMealPlans(mealPlansWithDetails);
        }
        setHasMore(data.length === pageSize);
      }
    } catch (err) {
      console.error('Error fetching meal plans:', err);
      setError('Failed to load meal plans. Please try again.');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchMealPlans(1);
  }, [searchQuery, user]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchMealPlans(nextPage, true);
  };

  const handleRowClick = (mealPlanId: string) => {
    navigate(`/meal-plans/${mealPlanId}`);
  };

  return (
    <MealPlansContainer>
      <MealPlansHeader>
        <HeaderContent>
          <TitleSection>
            <MealPlansTitle>Meal Plans</MealPlansTitle>
            <SubtitleMedium>Organize and manage your meal plans</SubtitleMedium>
            <FiltersContainer>
              <SearchInput>
                <input
                  type="text"
                  placeholder="Search meal plans..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <MagnifyingGlass size={16} />
              </SearchInput>
            </FiltersContainer>
          </TitleSection>
          <ActionButtons>
            <Button 
              icon={<Plus />} 
              onClick={() => navigate('/meal-plans/create')}
            >
              Create Meal Plan
            </Button>
            <AIButton 
              icon={<Sparkle weight="fill" />} 
              onClick={() => navigate('/meal-plans/create-ai')}
            >
              Create with AI Assistant
            </AIButton>
          </ActionButtons>
        </HeaderContent>
      </MealPlansHeader>

      <TableContainer>
        {isLoading ? (
          <LoadingMessage>Loading meal plans...</LoadingMessage>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : mealPlans.length === 0 ? (
          <EmptyState>No meal plans created yet. Create your first meal plan!</EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Cover</Th>
                  <Th>Title</Th>
                  <Th>Duration</Th>
                  <Th>Meals Included</Th>
                  <Th>Date Created</Th>
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {mealPlans.map((mealPlan) => (
                  <TableRow key={mealPlan.id} onClick={() => handleRowClick(mealPlan.id)}>
                    <Td>
                      <CoverImage 
                        src={mealPlan.cover_image || noImage} 
                        alt={mealPlan.title}
                      />
                    </Td>
                    <Td>{mealPlan.title}</Td>
                    <Td>{mealPlan.duration} week{mealPlan.duration !== 1 ? 's' : ''}</Td>
                    <Td>
                      <MealsList>
                        {mealPlan.meals.map((meal) => (
                          <MealTag key={meal}>{meal}</MealTag>
                        ))}
                      </MealsList>
                    </Td>
                    <Td>{new Date(mealPlan.created_at).toLocaleDateString()}</Td>
                    <Td>
                      <ActionButton onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(mealPlan.id);
                      }}>
                        View Details
                        <ArrowSquareOut size={20} />
                      </ActionButton>
                    </Td>
                  </TableRow>
                ))}
              </tbody>
            </Table>
            {hasMore && (
              <LoadMoreButton
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? 'Loading...' : 'Load More'}
              </LoadMoreButton>
            )}
          </>
        )}
      </TableContainer>
    </MealPlansContainer>
  );
} 